import { Injectable } from '@angular/core'
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http'
import { BehaviorSubject, catchError, filter, map, Observable, switchMap, take, tap, throwError } from 'rxjs'
import { RefreshTokenCommand } from './commands/refresh-token.command'
import { Optional } from '../data-types/optional.types'
import { JWTTokenService } from '../services/jwt-token'
import { authorizationRequest } from './auth-request.helper'

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  constructor(
    private jwtTokenService: JWTTokenService,
    private refreshTokenCommand: RefreshTokenCommand
  ) {}

  private isRefreshing = false
  private tokenSubject: BehaviorSubject<Optional<string>> = new BehaviorSubject<Optional<string>>(null)

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (request.headers.get('Authorization') && error.status === 401) {
          return this.isRefreshing ? this.deferRequest(request, next) : this.refreshToken(request, next)
        }
        return throwError(() => error)
      })
    )
  }

  private deferRequest(request: HttpRequest<unknown>, next: HttpHandler) {
    return this.tokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      map((token) => authorizationRequest(request, token!)),
      switchMap((authReq) => next.handle(authReq)),
      catchError((error) => {
        console.error('Error with new access token: logout')
        return throwError(() => error)
      })
    )
  }

  private refreshToken(request: HttpRequest<unknown>, next: HttpHandler) {
    this.isRefreshing = true
    this.tokenSubject.next(null)

    return this.refreshTokenCommand.call().pipe(
      tap((jwt) => {
        this.isRefreshing = false
        this.jwtTokenService.set(jwt)
        this.tokenSubject.next(jwt.access_token)
      }),
      map(({ access_token }) => authorizationRequest(request, access_token)),
      switchMap((authReq) =>
        next.handle(authReq).pipe(
          catchError((error) => {
            console.error('Error with new access token: logout')
            return throwError(() => error)
          })
        )
      ),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          this.jwtTokenService.remove()
          window.location.reload()
        }
        return throwError(() => error)
      })
    )
  }
}
